import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, MapPin, Trash2 } from 'lucide-react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { supabase } from '../../lib/supabase';
import {
  addressMarkerService,
  type AddressMarker,
} from '../../services/addressMarkerService';

/** Stable reference for useJsApiLoader — inline array retriggers LoadScript every render. */
const googleMapsLibraries: ('places')[] = ['places'];

interface OpportunitySalesPinCardProps {
  opportunityId: number;
}

/**
 * Read-only "Sales Pin" card for the CRM opportunity details drawer: when the
 * opportunity was created from a door-to-door sales pin, shows a small map
 * visual of the pin plus its knock-outcome status, address, and notes.
 * Renders nothing when no pin is linked.
 */
export default function OpportunitySalesPinCard({
  opportunityId,
}: OpportunitySalesPinCardProps) {
  const [pin, setPin] = useState<AddressMarker | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Bumped when a pin is created elsewhere (e.g. the create-menu drop flow)
  // so the card appears without reopening the drawer.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const handlePinCreated = () => setReloadKey((k) => k + 1);
    window.addEventListener('salesPinCreated', handlePinCreated);
    return () => window.removeEventListener('salesPinCreated', handlePinCreated);
  }, []);

  const handleDeletePin = async () => {
    if (!pin?.id || isDeleting) return;
    if (!window.confirm('Delete this sales pin? The lead stays — only the map pin is removed.')) return;
    setIsDeleting(true);
    try {
      await addressMarkerService.deleteMarker(pin.id);
      setPin(null);
      // Map views listen for this to refresh their pins.
      window.dispatchEvent(new CustomEvent('salesPinCreated'));
      toast.success('Sales pin deleted');
    } catch (err) {
      console.error('Error deleting sales pin:', err);
      toast.error('Failed to delete the sales pin');
    } finally {
      setIsDeleting(false);
    }
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: googleMapsLibraries,
  });

  useEffect(() => {
    let cancelled = false;
    setPin(null);
    if (!opportunityId || !supabase) return;

    supabase
      .from('address_markers')
      .select('*, customer:customers(id, first_name, last_name, type)')
      .eq('opportunity_id', opportunityId)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Error loading linked sales pin:', error);
          return;
        }
        setPin((data?.[0] as AddressMarker) ?? null);
      });

    return () => {
      cancelled = true;
    };
  }, [opportunityId, reloadKey]);

  if (!pin) return null;

  const color = addressMarkerService.getMarkerColor(pin.marker_type);
  const label = addressMarkerService.getMarkerTypeLabel(pin.marker_type);
  const position = { lat: pin.latitude, lng: pin.longitude };

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Sales Pin</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white"
            style={{ backgroundColor: color }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
            {label}
          </span>
          <button
            type="button"
            onClick={() => void handleDeletePin()}
            disabled={isDeleting}
            className="h-7 w-7 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50 active:scale-95 transition-all disabled:opacity-50"
            aria-label="Delete sales pin"
            title="Delete sales pin"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {isDeleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* Static map visual of the pin */}
      <div className="relative" style={{ height: 160 }}>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={position}
            zoom={17}
            options={{
              disableDefaultUI: true,
              gestureHandling: 'none',
              clickableIcons: false,
              keyboardShortcuts: false,
              mapTypeId: 'hybrid',
            }}
          >
            <Marker
              position={position}
              icon={addressMarkerService.getSalesPinIcon(
                pin.marker_type,
                addressMarkerService.getSalesPinInitials(pin),
              )}
            />
          </GoogleMap>
        ) : (
          <div className="absolute inset-0 bg-gray-100 animate-pulse" />
        )}
      </div>

      {(pin.address || pin.notes || pin.google_place_name || pin.google_place_phone || pin.google_place_website) && (
        <div className="px-4 py-3 space-y-1">
          {pin.address && (
            <p className="text-xs text-gray-600 leading-snug">{pin.address}</p>
          )}
          {(pin.google_place_name || pin.google_place_phone || pin.google_place_website) && (
            <div className="mt-1.5 px-2.5 py-2 bg-blue-50/70 border border-blue-100 rounded-lg">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-500 mb-0.5">Google Business</p>
              {pin.google_place_name && (
                <p className="text-xs font-semibold text-gray-900">{pin.google_place_name}</p>
              )}
              {pin.google_place_phone && (
                <a href={`tel:${pin.google_place_phone}`} className="block text-xs text-blue-700 mt-0.5">
                  {pin.google_place_phone}
                </a>
              )}
              {pin.google_place_website && (
                <a
                  href={pin.google_place_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-blue-700 mt-0.5 truncate"
                >
                  {pin.google_place_website}
                </a>
              )}
            </div>
          )}
          {pin.notes && (
            <p className="text-xs text-gray-400 leading-snug whitespace-pre-wrap">{pin.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}
